/* eslint-disable complexity */
import LogoutIcon from '@mui/icons-material/LogoutRounded';
import TuneIcon from '@mui/icons-material/TuneRounded';
import { List, useMediaQuery, useTheme } from '@mui/material';
import * as Sentry from '@sentry/nextjs';
import useCanViewOrgPage from 'hooks/useCanViewOrgPage';
import useIsAnonymousUser from 'hooks/useIsAnonymousUser';
import useProfile from 'hooks/useProfile';
import { useTranslation } from 'next-i18next';
import { useRouter } from 'next/router';
import { useCallback } from 'react';
import type { FC } from 'react';

import pageUrls from 'constants/pageUrls';

import AvatarFromDesign from 'components/AvatarFromDesign';
import DashboardIcon from 'components/Icons/DashboardIcon';
import DeviceGroupsIcon from 'components/Icons/DeviceGroupsIcon';
import DevicesIcon from 'components/Icons/DevicesIcon';
import LoadBalancingIcon from 'components/Icons/LoadBalancingIcon';
import OrganizationsIcon from 'components/Icons/OrganizationsIcon/intex';
import RfidGroupsIcon from 'components/Icons/RfidGroupsIcon';
import RfidsIcon from 'components/Icons/RfidsIcon';

import { useAppDispatch } from 'redux-store';
import { closeSidebar } from 'redux-store/slices/toggleSidebarMenu';
import { setProfileModal } from 'redux-store/slices/ui';
import logoutThunk from 'redux-store/thunks/profile/logoutThunk';

import NavLink from './NavLink';
import SidebarGroup from './SidebarGroup';

interface SidebarItemsProps {
  isSidebarExpanded: boolean;
}

const SidebarItems: FC<SidebarItemsProps> = ({ isSidebarExpanded }) => {
  const router = useRouter();
  const dispatch = useAppDispatch();
  const isAnonymous = useIsAnonymousUser();
  const { t } = useTranslation();
  const profile = useProfile();
  const theme = useTheme();
  const shouldShowOrgPage = useCanViewOrgPage();

  const lgUp = useMediaQuery(theme.breakpoints.up('lg'));

  const handleOpenProfile = useCallback(() => {
    dispatch(setProfileModal(true));
  }, [dispatch]);

  const handleLogout = useCallback(async (): Promise<void> => {
    await dispatch(logoutThunk());

    if (!lgUp && isSidebarExpanded) {
      dispatch(closeSidebar());
    }
    Sentry.setUser(null);

    await router.push(pageUrls.login, undefined, {
      locale: router.locale,
    });
  }, [dispatch, lgUp, router, isSidebarExpanded]);

  return (
    <List
      sx={{
        pt: 0,
        pb: 3,
        px: 1,
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
      }}
      className="sidebarNav"
      component="div"
    >
      <SidebarGroup>
        <NavLink
          isActive={router.pathname === pageUrls.homeDashboard}
          linkProps={{
            href: pageUrls.homeDashboard,
            'data-testid': 'dashboard-button',
          }}
          listItemTextProps={{
            primary: t('common:dashboard'),
          }}
          renderIcon={<DashboardIcon />}
          collapsed={!isSidebarExpanded}
        />
      </SidebarGroup>
      <SidebarGroup>
        <NavLink
          isActive={
            router.pathname === pageUrls.devices.all ||
            router.pathname === pageUrls.devices.specificDeviceTypeGeneric ||
            router.pathname === pageUrls.device.devicePageGeneric ||
            router.pathname.startsWith(pageUrls.settingsPageGeneric)
          }
          linkProps={{
            href: {
              pathname: pageUrls.devices.all,
              query: {
                deviceType: router.query.deviceType ?? 'all',
              },
            },
            'data-testid': 'all-devices-button',
          }}
          listItemTextProps={{
            primary: t('common:pages.devices.page_title'),
          }}
          renderIcon={<DevicesIcon />}
          collapsed={!isSidebarExpanded}
          data-testid="all-devices"
        />
        <NavLink
          isActive={
            router.pathname === pageUrls.deviceGroups.all ||
            router.pathname.startsWith(
              pageUrls.deviceGroup.deviceGroupPageGeneric,
            )
          }
          linkProps={{
            href: pageUrls.deviceGroups.all,
            'data-testid': 'device-groups-button',
          }}
          listItemTextProps={{
            primary: t('common:pages.device_groups.page_title'),
          }}
          renderIcon={<DeviceGroupsIcon />}
          collapsed={!isSidebarExpanded}
          hidden={isAnonymous}
        />
        <NavLink
          isActive={router.pathname === pageUrls.loadBalancing}
          linkProps={{
            href: pageUrls.loadBalancing,
            'data-testid': 'load-balancing-button',
          }}
          listItemTextProps={{
            primary: t('common:pages.load_balancing.page_title'),
          }}
          renderIcon={<LoadBalancingIcon />}
          collapsed={!isSidebarExpanded}
          hidden={isAnonymous}
        />
        <NavLink
          isActive={router.pathname === pageUrls.organizations}
          linkProps={{
            href: pageUrls.organizations,
            'data-testid': 'organizations-button',
          }}
          listItemTextProps={{
            primary: t('common:pages.organization.page_title'),
          }}
          renderIcon={<OrganizationsIcon />}
          collapsed={!isSidebarExpanded}
          hidden={!isAnonymous && shouldShowOrgPage ? false : true}
        />
      </SidebarGroup>
      <SidebarGroup hidden={isAnonymous}>
        <NavLink
          isActive={
            router.pathname === pageUrls.rfids.all ||
            router.pathname.startsWith(pageUrls.rfid.rfidPageGeneric)
          }
          linkProps={{
            href: pageUrls.rfids.all,
            'data-testid': 'rfids-button',
          }}
          listItemTextProps={{
            primary: t('common:pages.rfids.page_title'),
          }}
          renderIcon={<RfidsIcon />}
          collapsed={!isSidebarExpanded}
        />
        <NavLink
          isActive={
            router.pathname === pageUrls.rfidGroups.all ||
            router.pathname.startsWith(pageUrls.rfidGroup.rfidGroupPageGeneric)
          }
          linkProps={{
            href: pageUrls.rfidGroups.all,
            'data-testid': 'rfid-groups-button',
          }}
          listItemTextProps={{
            primary: t('common:pages.rfid_groups.page_title'),
          }}
          renderIcon={<RfidGroupsIcon />}
          collapsed={!isSidebarExpanded}
        />
      </SidebarGroup>

      <SidebarGroup alignment="bottom">
        <NavLink
          listItemTextProps={{
            primary: profile?.displayName ?? profile?.email,
            secondary: profile?.displayName ? profile.email : undefined,
            primaryTypographyProps: {
              sx: {
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              },
            },
            secondaryTypographyProps: {
              sx: {
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              },
            },
            title: profile?.displayName || profile?.email || undefined,
          }}
          listItemButtonProps={{
            onClick: () => handleOpenProfile(),
            'data-testid': 'profile-email-button',
          }}
          listItemIconProps={{
            sx: {
              width: 32,
              height: 32,
            },
          }}
          renderIcon={<AvatarFromDesign sx={{ width: 32, height: 32 }} />}
          collapsed={!isSidebarExpanded}
          hidden={isAnonymous}
        />
        <NavLink
          isActive={router.pathname === pageUrls.generalSettings.all}
          linkProps={{
            href: pageUrls.generalSettings.all,
            'data-testid': 'general-settings-button',
          }}
          listItemTextProps={{
            primary: t('common:pages.general_settings.page_title'),
          }}
          renderIcon={<TuneIcon />}
          collapsed={!isSidebarExpanded}
        />
        <NavLink
          listItemTextProps={{
            primary: t('common:logout'),
          }}
          listItemButtonProps={{
            onClick: handleLogout,
            'data-testid': 'logout-button',
          }}
          listItemIconProps={{
            sx: {
              width: 32,
              height: 32,
            },
          }}
          renderIcon={<LogoutIcon />}
          collapsed={!isSidebarExpanded}
        />
      </SidebarGroup>
    </List>
  );
};

export default SidebarItems;
